/* global SGAction, sgData */
import TaggedLogger from '../../debug/tagged-logger';

const logger = new TaggedLogger('PaymentWidgets', 'color:#43bfb6');

/**
 * AffirmPaymentWidget
 */
class AffirmPaymentWidget {
  /**
   * Init affirm.js external script and callbacks
   * @param {Object} _affirmConfig - public_api_key and script_url
   * @param {Object} global - set global (for unit testing)
   */
  constructor(_affirmConfig = {}, global = window) {
    this.global = global;
    this.loadAffirm(_affirmConfig);
    this.readyCallback = this.readyCallback.bind(this);
    this.PDPcore = this.global.ShopgateProductDetailsCore;
  }

  /**
   * Init default stuff
   */
  initDefault() {
    this.global.affirm.ui.ready(this.readyCallback);
  }

  /**
   * Get the widget element
   * @return {*}
   */
  static getElement() {
    let affirmWidget;
    const widgets = document.getElementsByClassName('payment-widget');
    for (let i = 0; i < widgets.length; i += 1) {
      if (widgets[i] && widgets[i].className.indexOf('affirm') !== -1) {
        affirmWidget = widgets[i];
      }
    }
    return affirmWidget;
  }

  get shouldToggleMenuBar() {
    let toggleMenuBar = true;
    if (
      typeof this.global.pageInformation !== 'undefined'
      && typeof this.global.pageInformation.menuBarVisible !== 'undefined'
    ) {
      toggleMenuBar = this.global.pageInformation.menuBarVisible;
    }
    return toggleMenuBar;
  }

  /**
   * The affirm.ui.ready callback
   */
  readyCallback() {
    const affirmWidget = this.constructor.getElement();

    if (!affirmWidget) {
      return;
    }

    let toggleMenuBar = false;

    this.global.affirm.setAffirmConfig(affirmWidget, {
      onCloseModal: () => {
        if (toggleMenuBar) {
          SGAction.showMenuBar();
        }

        if (SGAction.setScrollingEnabled) {
          SGAction.setScrollingEnabled(true);
        }

        /* Yep.. same class on production */
        const modals = document.getElementsByClassName('affirm-sandbox-container');
        for (let i = 0; i < modals.length; i += 1) {
          modals[i].remove();
        }
      },
      onOpenModal: () => {
        if (SGAction.setScrollingEnabled) {
          SGAction.setScrollingEnabled(false);
        }
      },
    });

    /* Prevent double opening of modal */
    affirmWidget.addEventListener('click', (e) => {
      e.preventDefault();
      toggleMenuBar = this.shouldToggleMenuBar;

      if (toggleMenuBar) {
        SGAction.hideMenuBar();
      }
      return false;
    });
  }

  /**
   * Snippet from affirm to init the script - should not be touched
   * @see https://docs.affirm.com/Integrate_Affirm/Promotional_Messaging
   * @param {Object} _affirmConfig - public_api_key and script_url
   */
  loadAffirm(_affirmConfig = {}) {
    /*
    eslint-disable
    no-param-reassign , semi, no-plusplus, no-shadow, one-var-declaration-per-line,
    curly, one-var, indent, no-var, prefer-rest-params, wrap-iife
    */
    (function (l, g, m, e, a, f, b) {
      var d, c = l[m] || {}, h = document.createElement(f), n = document.getElementsByTagName(f)[0],
          k = function (a, b, c) {
            return function () {
              a[b]._.push([c, arguments])
            }
          };
      c[e] = k(c, e, 'set');
      d = c[e];
      c[a] = {};
      c[a]._ = [];
      d._ = [];
      c[a][b] = k(c, a, b);
      a = 0;
      for (b = 'set add save post open empty reset on off trigger ready setProduct'.split(' '); a < b.length; a++) d[b[a]] = k(c, e, b[a]);
      a = 0;
      for (b = ['get', 'token', 'url', 'items']; a < b.length; a++) d[b[a]] = function () {
      };
      h.async = !0;
      h.src = g[f];
      n.parentNode.insertBefore(h, n);
      delete g[f];
      d(g);
      l[m] = c
    })(this.global, _affirmConfig, 'affirm', 'checkout', 'ui', 'script', 'ready');
    /* eslint-enable */
  }

  /**
   * Initialise a product details page plugin
   */
  initPDPplugin() {
    if (typeof this.PDPcore === 'object' && typeof this.PDPcore.registerPlugin === 'function') {
      this.global.affirm.ui.ready(() => {
        const p = this.PDPcore.registerPlugin(this.createPlugin());

        if (p && this.PDPcore.isInitialized()) {
          p.onCoreInit('products.ui', {
            coreOptions: this.PDPcore.getCoreOptions(),
            products: this.PDPcore.products,
          });
        }
      });
    } else {
      logger.error('No instance of "ShopgateProductDetailsCore" found');
    }
  }

  /**
   * Get the plugin
   * @return {function(SGProductDetailsCore)}
   */
  createPlugin() {
    const that = this;
    const element = this.constructor.getElement();

    /**
     * Product details script plugin that manages the payment widget
     *
     * @param {SGProductDetailsCore} core - Instance of SGProductDetailsCore
     */
    const ShopgatePaymentWidgetsPlugin = function (core) {
      const plugin = this;
      plugin.vendor = 'Shopgate';
      plugin.name = 'ShopgatePaymentWidgetsPlugin';
      plugin.type = ['products.ui'];

      /**
       * Set amount to element and update affirm ui
       * @param {number} amount -
       */
      const updateWidget = (amount = 0) => {
        element.setAttribute('data-amount', amount);
        that.global.affirm.ui.refresh();
      };

      /**
       * Plugin initialization.
       * @param {string} callType - products.ui
       * @param {Object} data - initial product data
       */
      plugin.onCoreInit = (callType, data) => {
        logger.info('init plugin', data, element);

        that.readyCallback();

        const current = core.getSelectedProduct();
        const values = core.getSelectedValues();

        if (!current || !values || values.product_id !== current.product.id) {
          return;
        }

        updateWidget(current.product.price_info.unit_amount_display * values.quantity);
      };

      /**
       * Check the updated product
       * @param {string} callType - products.ui
       * @param {Object} data - product data of the currently updated product
       */
      plugin.onUpdateProduct = (callType, data) => {
        logger.info('update product', data);

        if (!data.product_price_info) {
          return;
        }

        updateWidget(data.product_price_info.unit_amount_display);
      };

      /**
       * Is called on any setState trigger.
       *
       * @param {string} callType - products.ui
       * @param {Object} data - productId and quantity
       */
      plugin.onUpdateCartProductQuantity = (callType, data) => {
        logger.info('update quantity', data);

        const current = core.getSelectedProduct();

        if (data.productId !== current.product.id) {
          return;
        }

        updateWidget(current.product.price_info.unit_amount_display * data.quantity);
      };
    };

    return ShopgatePaymentWidgetsPlugin;
  }
}

export default AffirmPaymentWidget;



// WEBPACK FOOTER //
// ./Library/1.0/common/payment_widgets/widgets/AffirmPaymentWidget.js